/**
 * When the current trading session opened, from the engine.
 *
 * Fetched rather than computed. The boundary is 5:02pm CT with DST and holidays
 * on top, owned by `session::session_start_ms` in Rust; a second implementation
 * in TypeScript is the duplication this repo bans, and `srStore.ts` had to fall
 * back to calendar days precisely because nothing exposed it. Now something
 * does.
 *
 * `null` until the first response, so callers can fall back to a rolling window
 * instead of scoping to a boundary they are guessing at.
 */

import { useEffect, useState } from 'react';
import { CONFIG } from '../config/ports';

/** The session rolls once a day; this only has to be timely near the roll. */
const REFRESH_MS = 5 * 60 * 1000;

export interface SessionWindow {
  sessionStartMs: number;
  /** The engine's clock. Used instead of the browser's so a skewed local clock
   *  scopes the same window the engine would rather than one shifted by the
   *  difference. */
  nowMs: number;
}

export function useSessionStart(): SessionWindow | null {
  const [session, setSession] = useState<SessionWindow | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const base = CONFIG.getBackendUrl(window.location.hostname);
        const res = await fetch(`${base}/api/session/current`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (typeof data?.sessionStartMs === 'number' && typeof data?.nowMs === 'number') {
          setSession({ sessionStartMs: data.sessionStartMs, nowMs: data.nowMs });
        }
      } catch {
        // A background poll for a display window. Failing quietly leaves the
        // caller on its rolling fallback; a toast here would fire on every
        // engine restart for something the user did not ask for.
      }
    };

    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  return session;
}
